import { lazy, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, Navigate } from "react-router-dom";

import { Loader } from "@dexibit/core";
import { Skeleton } from "@mui/material";
import { PALETTE_LIGHT } from "@dexibit/styles";

import ErrorFallback from "./components/ErrorFallback";
import { Navigation } from "./components/Navigation";
import { OutageBanner } from "./components/OutageBanner";
import {
  useAppListeners,
  useDebouncedError,
  useInit,
  useIntercom,
  useModules,
  usePendo,
  useUserContext,
} from "./hooks";
import useLaunchDarkly from "./hooks/useLaunchDarkly";
import FourOhFourPage from "./pages/FourOhFourPage";
import IFramePage from "./pages/IFramePage";
import {
  AppGrid,
  AppContent,
  LoadingContainer,
  ErrorContainer,
  MegaHeaderArea,
  NavigationArea,
  MainContentArea,
} from "./styles";
import logger from "./utils/logger";
import { MEGA_ADMIN_ACCOUNT_ID, MegaModeHeader } from "./components/MegaModeHeader";
import { getSublinkRoutes } from "./utils/url";
import { NavigationLink } from "./types";

const AskPage = lazy(() => import("./pages/AskPage"));
const ChatLayout = lazy(() =>
  import("./features/data-explorer/pages/ChatPage/layouts/ChatLayout").then((m) => ({
    default: m.ChatLayout,
  })),
);
const WelcomeChat = lazy(() =>
  import("./features/data-explorer/pages/ChatPage/WelcomeChat/index").then((m) => ({
    default: m.WelcomeChat,
  })),
);
const ConversationGuard = lazy(() =>
  import("./features/data-explorer/pages/ChatPage/ConversationGuard").then((m) => ({
    default: m.ConversationGuard,
  })),
);
const ChatMissingPage = lazy(() =>
  import("./features/data-explorer/pages/ChatPage/ChatMissingPage").then((m) => ({
    default: m.ChatMissingPage,
  })),
);
const KnowledgePage = lazy(() =>
  import("./features/data-explorer/pages/KnowledgePage/KnowledgePage").then((m) => ({
    default: m.KnowledgePage,
  })),
);

const NATIVE_ROUTE_KEYS = ["/v2/ask"];

const LegacyRedirectPaths = ["/v2", "/v2/home", "/v2/app"];
const LegacyRedirect = ({ links }: { links: NavigationLink[] }) => {
  return links[0] ? <Navigate to={links[0]?.to} replace /> : <FourOhFourPage />;
};

const HomeToAskRedirect = () => {
  return <Navigate to="/v2/ask/rex" replace />;
};

const App = () => {
  const { authenticated, userInfo, isInitError, isInitLoading, isUninitialized, config } =
    useInit();
  const { user, account, venue, isUserContextError, isUserContextLoading } =
    useUserContext();
  const { links, activeLink, isModulesError, hasLinks } = useModules(user, account);

  const hasMegaModeHeader = account?.id === MEGA_ADMIN_ACCOUNT_ID;

  const gridClassName = hasMegaModeHeader ? "has-mega-header" : "";

  useLaunchDarkly(user);

  useIntercom(user, venue, account);
  usePendo(user, venue);

  const { dialogVisible } = useAppListeners(user, config);

  const appIsLoading =
    isUninitialized ||
    isInitLoading ||
    isUserContextLoading ||
    !authenticated ||
    !hasLinks;
  const appHasError = isInitError || isModulesError || isUserContextError;

  // Debounce error display to prevent flashing during initial load
  const shouldShowError = useDebouncedError(appHasError, appIsLoading);

  if (shouldShowError) {
    return (
      <AppGrid className={gridClassName}>
        <ErrorContainer>
          <ErrorFallback />
        </ErrorContainer>
      </AppGrid>
    );
  }

  if (appIsLoading) {
    return (
      <AppGrid className={gridClassName}>
        <LoadingContainer>
          <Loader size="large" />
        </LoadingContainer>
      </AppGrid>
    );
  }

  return (
    <AppGrid className={gridClassName}>
      {hasMegaModeHeader && (
        <MegaHeaderArea>
          <MegaModeHeader />
        </MegaHeaderArea>
      )}
      <NavigationArea>
        <Navigation
          links={links}
          activeLink={activeLink}
          dialogVisible={dialogVisible}
          userInfo={userInfo}
          accountName={account?.name}
          venueName={venue?.name}
        />
      </NavigationArea>
      <MainContentArea>
        <OutageBanner />
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error: Error) => logger.error("Application error", error)}
        >
          <AppContent>
            <Routes>
              {LegacyRedirectPaths.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={<LegacyRedirect links={links} />}
                />
              ))}
              {/* Native routes for DE */}
              <Route path="/v2/ask" element={<Navigate to="/v2/ask/rex" replace />} />
              <Route
                path="/v2/ask/rex"
                element={
                  <Suspense
                    fallback={
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width="100%"
                        height="100%"
                        sx={{
                          bgcolor: PALETTE_LIGHT.COMPONENT_BORDER,
                          "&::after": {
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                          },
                        }}
                      />
                    }
                  >
                    <AskPage />
                  </Suspense>
                }
              >
                <Route element={<ChatLayout />}>
                  <Route index element={<WelcomeChat />} />
                  <Route path="knowledge" element={<KnowledgePage />} />
                  <Route path=":conversationId" element={<ConversationGuard />} />
                  <Route path="404" element={<ChatMissingPage />} />
                </Route>
              </Route>
              {/* Iframe routes (excluding native routes) */}
              {getSublinkRoutes(links)
                .filter(
                  (route) => !NATIVE_ROUTE_KEYS.some((key) => route.key.startsWith(key)),
                )
                .map((route) => (
                  <Route
                    key={route.key}
                    path={route.path}
                    element={
                      route.redirectTo ? (
                        <Navigate to={route.redirectTo} replace />
                      ) : (
                        <IFramePage basePath={route.basePath} iframe={route.iframe!} />
                      )
                    }
                  />
                ))}
              <Route path="/v2/home/*" element={<HomeToAskRedirect />} />
              <Route path="*" element={<FourOhFourPage />} />
            </Routes>
          </AppContent>
        </ErrorBoundary>
      </MainContentArea>
    </AppGrid>
  );
};

export default App;
